import { IValidationFunction } from "aic";

const CRYSTAL_TICKET_REGEX = /^cr-\d{6}$/i;
const CRYSTAL_VALIDATION_ERROR =
  "Invalid format. Expected: cr-XXXXXX (6 digits), separated by commas";

/**
 * Synchronously checks whether a CRYSTAL reference value is valid.
 * An empty or undefined value is considered valid (the field is optional).
 * A comma-separated list is valid only when every non-empty entry matches
 * the pattern `cr-XXXXXX` (case-insensitive, exactly six digits), and the
 * list contains no empty segments (e.g. a trailing comma is invalid).
 *
 * @param value - The crystal reference string to validate, or undefined.
 * @returns `true` when the value is valid or absent, `false` otherwise.
 */
export const isCrystalReferenceValid = (value: string | undefined): boolean => {
  const normalised = (value ?? "").trim();
  if (normalised === "") return true;
  const tickets = normalised.split(",").map((v) => v.trim());
  if (tickets.some((t) => t === "")) return false;
  return tickets.every((t) => CRYSTAL_TICKET_REGEX.test(t));
};

/**
 * Validates CRYSTAL ticket number format (cr-XXXXXX where X is a digit)
 *  for a comma-separated list of ticket numbers.
 *
 * @param _backEndUrl - Unused here; kept for IValidationFunction signature
 * @param target - Validation target containing the value to check
 * @returns Promise resolving to validation result
 */
export const validateCrystalTicketNumber: IValidationFunction = async (_backEndUrl, target) => {
  const valid = isCrystalReferenceValid(target.value);
  return {
    success: true,
    data: {
      valid,
      validationText: valid ? "" : CRYSTAL_VALIDATION_ERROR,
    },
  };
};
